import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ParticipantModel, ParticipantsResponse } from '@shared/models/participant.model';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CurrentProgression, Level, MenuModel, Module, Step, StepDataPerSlug } from '@shared/models/menu.model';
import Bugsnag from '@bugsnag/js';
import { Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { ZendeskService } from '@shared/services/zendesk.service';
import { UrlEndpoint } from '@app/utils/url';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import { I18nService } from '@app/i18n';

export interface ParticipantProgression {
  linkNextLifeActs?: string;
  linkNextStep?: string;
  moduleTitle?: string;
  moduleName?: string;
  stepTitle?: string;
  stepName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ParticipantService {
  private _participant: WritableSignal<ParticipantModel | null> = signal(null);
  private _menu: WritableSignal<MenuModel | null> = signal(null);
  private _stepDataPerSlug: WritableSignal<StepDataPerSlug | null> = signal(null);

  public participantTimeFormat: Signal<TimeFormat> = computed(() => {
    return this.participant()?.cohorts[0]?.timeFormat || TimeFormat.twentyFourHours;
  });
  public participantProgression: Signal<ParticipantProgression | null> = computed(() =>
    this.getParticipantProgression(this.menu()?.current_progression),
  );

  private _toggleLeftMenu: Subject<void> = new Subject<void>();

  constructor(
    private httpService: HttpService,
    private i18n: I18nService,
    private zendeskService: ZendeskService,
  ) {}

  public retrieveMenu(): Promise<MenuModel> {
    return new Promise((resolve, reject): void => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.GET,
        url: `/menu`,
      };

      this.httpService.request(requestOptions).subscribe({
        next: (menu: MenuModel): void => {
          if (menu) {
            this.setStepDataPerSlug(menu.menudata[0].children);
            this.setMenu(menu);
            resolve(menu);
          } else {
            reject();
          }
        },
        error: (error: HttpErrorResponse): void => {
          reject(error);
        },
      });
    });
  }

  public retrieveParticipant(): Promise<ParticipantModel> {
    return new Promise((resolve, reject) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.GET,
        url: UrlEndpoint.participants,
      };

      this.httpService.request(requestOptions).subscribe({
        next: (response: ParticipantsResponse) => {
          if (response) {
            const participant: ParticipantModel = response.data;
            this.setParticipant(participant);
            resolve(participant);
          } else {
            reject();
          }
        },
        error: (error: HttpErrorResponse) => {
          reject(error);
        },
      });
    });
  }

  public isParticipantDataRetrieved(): boolean {
    return !!this.menu() && !!this.participant();
  }

  public get participant(): Signal<ParticipantModel | null> {
    return this._participant.asReadonly();
  }

  public setParticipant(participant: ParticipantModel | null): void {
    this._participant.set(participant);

    if (environment.production) {
      this.setUpBugsnagForUser();
    }

    const fullName: string = this.getParticipantFullName();
    this.zendeskService.setUpZendeskForUser(fullName, participant?.email);
  }

  public get menu(): Signal<MenuModel | null> {
    return this._menu.asReadonly();
  }

  public setMenu(menu: MenuModel | null): void {
    this._menu.set(menu);
  }

  private setStepDataPerSlug(levels: Level[]): void {
    const stepDataPerSLug: StepDataPerSlug = {};
    levels.forEach((level: Level) => {
      level.children.forEach((module: Module) => {
        module.children.forEach((step: Step) => {
          stepDataPerSLug[step.fullUrl] = step;
        });
      });
    });

    this._stepDataPerSlug.set(stepDataPerSLug);
  }

  public toggleLeftMenu(): void {
    this._toggleLeftMenu.next();
  }

  public getToggleLeftMenu(): Observable<void> {
    return this._toggleLeftMenu.asObservable();
  }

  private setUpBugsnagForUser(): void {
    const participant: ParticipantModel | null = this.participant();
    if (participant) {
      Bugsnag.setUser(`${participant.id}`, participant.email, this.getParticipantFullName());

      if (participant.cohorts.length > 0) {
        Bugsnag.addMetadata('cohort', {
          id: participant.cohorts[0].id,
          code: participant.cohorts[0].code,
        });
        Bugsnag.addMetadata('company', {
          id: participant.cohorts[0].company.id,
          name: participant.cohorts[0].company.name,
        });
      }
    } else {
      Bugsnag.setUser();
      Bugsnag.clearMetadata('cohort');
      Bugsnag.clearMetadata('company');
    }
  }

  public getParticipantFullName(): string {
    const participant: ParticipantModel | null = this.participant();
    return participant ? `${participant.first_name} ${participant.last_name}` : '';
  }

  private getParticipantProgression(currentProgression: CurrentProgression | undefined): ParticipantProgression | null {
    if (currentProgression) {
      return {
        linkNextLifeActs: currentProgression.current_lifeact_step?.fullUrl,
        linkNextStep: currentProgression.current_step?.fullUrl,
        moduleTitle: currentProgression.current_module?.title,
        moduleName: currentProgression.current_module?.name,
        stepTitle: currentProgression.current_step?.title,
        stepName: currentProgression.current_step?.name,
      };
    }

    return null;
  }
}
